import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api, { processFOIADocument, redactPDFForFOIA, analyzePDFForFOIA, getFOIARule, processPDFForFOIA, generateRedactedPDF, getApiBaseUrl, getDiscoveryFile, generateFOIALetter } from '../services/api';
import { DocumentArrowUpIcon, DocumentTextIcon, EyeSlashIcon, ExclamationTriangleIcon, CheckCircleIcon, DocumentArrowDownIcon, LinkIcon, XMarkIcon, PencilSquareIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import MappingFailureDisplay from '../components/MappingFailureDisplay';
import FullScreenRedactionEditor from '../components/redaction/FullScreenRedactionEditor';
import PDFRedactionEditor from '../components/redaction/PDFRedactionEditor';
import ProcessingStatus from '../components/ProcessingStatus';

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function FOIAProcessing() {
  const location = useLocation();
  const [processing, setProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('Processing...');
  const [processingPhase, setProcessingPhase] = useState(null);
  const [text, setText] = useState('');
  const [agency, setAgency] = useState('');
  const [requesterInterest, setRequesterInterest] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [processingMode, setProcessingMode] = useState('redact'); // Always use 'redact' for interactive mode
  const [useEnhancedProcessing, setUseEnhancedProcessing] = useState(true);
  const [conflictResolution, setConflictResolution] = useState('merge');
  // F10: which redaction source(s) the enhanced pipeline uses when standard
  // exemption detection and rule-based detection disagree. 'both' matches
  // today's default behavior.
  const [conflictResolutionMode, setConflictResolutionMode] = useState('both');
  const [redactedPDFData, setRedactedPDFData] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [loadingRule, setLoadingRule] = useState(false);
  
  // Interactive redaction editor state
  const [showRedactionEditor, setShowRedactionEditor] = useState(false);
  const [showPDFRedactionEditor, setShowPDFRedactionEditor] = useState(false);
  const [showApprovalWorkflow, setShowApprovalWorkflow] = useState(false);
  const [editableRedactions, setEditableRedactions] = useState([]);
  const [originalDocumentText, setOriginalDocumentText] = useState('');
  const [currentPDFFile, setCurrentPDFFile] = useState(null);
  const [selectedUploadFile, setSelectedUploadFile] = useState(null);

  // Grouped redactions UI state
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedRedactions, setExpandedRedactions] = useState(new Set());
  const [showProcessingDetails, setShowProcessingDetails] = useState(false);

  // FOIA Response Letter state
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [letterResult, setLetterResult] = useState(null);
  const [letterLoading, setLetterLoading] = useState(false);
  const [letterForm, setLetterForm] = useState({
    requester_name: '',
    request_date: new Date().toISOString().slice(0, 10),
    request_description: '',
    agency_name: 'Department of Defense',
    response_type: 'partial_release',
  });

  // Group redactions by exemption category
  const groupedRedactions = useMemo(() => {
    if (!result?.redactions) return {};
    const groups = {};
    result.redactions.forEach((redaction, index) => {
      const key = redaction.exemption || 'unspecified';
      if (!groups[key]) groups[key] = [];
      groups[key].push({ ...redaction, _idx: index });
    });
    return groups;
  }, [result]);

  // Auto-expand first group when results arrive
  useEffect(() => {
    const keys = Object.keys(groupedRedactions);
    if (keys.length > 0) {
      setExpandedGroups(new Set([keys[0]]));
      setExpandedRedactions(new Set());
    }
  }, [groupedRedactions]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleRedaction = (idx) => {
    setExpandedRedactions(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const expandAllGroups = () => setExpandedGroups(new Set(Object.keys(groupedRedactions)));
  const collapseAllGroups = () => { setExpandedGroups(new Set()); setExpandedRedactions(new Set()); };

  // Restore state from query history or handle discovered document from FOIA Discovery
  useEffect(() => {
    // Handle document from FOIA Discovery page
    const discoveredDocument = location.state?.discoveredDocument;
    if (discoveredDocument) {
      // Clear location state to prevent re-restoration
      window.history.replaceState({}, document.title);

      // If we have a file path, fetch the actual file and process it
      if (discoveredDocument.filePath) {
        const fetchAndProcessDiscoveredFile = async () => {
          try {
            setProcessing(true);
            setProcessingProgress(10);
            setProcessingMessage('Loading discovered document...');
            setError(null);
            setResult(null);
            setRedactedPDFData(null);

            const blob = await getDiscoveryFile(discoveredDocument.filePath);
            const file = new File([blob], discoveredDocument.filename || 'document', {
              type: blob.type || 'application/octet-stream',
            });

            setCurrentPDFFile(file);

            // Process the file through the PDF FOIA endpoint (handles all file types)
            setProcessingProgress(25);
            setProcessingMessage('Analyzing document for FOIA exemptions...');

            const pdfResult = await processPDFForFOIA(file, agency, requesterInterest);

            // Handle converted files - create a File object from base64 for PDF editor
            if (pdfResult.converted_pdf_base64) {
              const byteCharacters = atob(pdfResult.converted_pdf_base64);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

              const originalName = pdfResult.original_filename || file.name;
              const baseName = originalName.replace(/\.[^/.]+$/, '');
              const pdfFile = new File([pdfBlob], `${baseName}_converted.pdf`, { type: 'application/pdf' });
              setCurrentPDFFile(pdfFile);
            }

            setProcessingProgress(80);
            setProcessingMessage('Mapping redactions to document...');

            setResult(pdfResult.text_analysis);
            setOriginalDocumentText(pdfResult.text_analysis.original_text || '');
            setText(pdfResult.text_analysis.original_text || discoveredDocument.contentPreview || '');

            // Use coordinate_redactions if available, otherwise fall back to text redactions
            const redactionsToUse = pdfResult.text_analysis.coordinate_redactions || pdfResult.text_analysis.redactions || [];
            setEditableRedactions(redactionsToUse);

            // Set up legacy data for backward compatibility
            setRedactedPDFData({
              textAnalysis: pdfResult.text_analysis,
              redactionCount: pdfResult.redaction_count,
              exemptionsUsed: pdfResult.exemptions_used,
              wasConverted: !!pdfResult.converted_pdf_base64,
              conversionMetadata: pdfResult.conversion,
              mappingStats: pdfResult.mappingStats
            });

            setProcessingProgress(100);
            setProcessingMessage('Complete');
          } catch (err) {
            console.error('Failed to load/process discovered document:', err);
            setError(`Failed to process document: ${err.response?.data?.detail || err.message}`);
            // Fall back to content preview if available
            if (discoveredDocument.contentPreview) {
              setText(discoveredDocument.contentPreview);
              setOriginalDocumentText(discoveredDocument.contentPreview);
            }
          } finally {
            setProcessing(false);
          }
        };

        fetchAndProcessDiscoveredFile();
      } else if (discoveredDocument.contentPreview) {
        // Fallback: just use the content preview if no file path
        setText(discoveredDocument.contentPreview);
        setOriginalDocumentText(discoveredDocument.contentPreview);
      }
      return; // Don't process restoredQuery if we have discoveredDocument
    }

    const restoredQuery = location.state?.restoredQuery;
    if (restoredQuery && restoredQuery.query_type === 'foia') {
      // Restore input text
      if (restoredQuery.input_text) {
        setText(restoredQuery.input_text);
        setOriginalDocumentText(restoredQuery.input_text);
      }

      // Restore parameters (input_params from query history, or parameters for backward compat)
      const params = restoredQuery.input_params || restoredQuery.parameters;
      if (params) {
        if (params.agency) {
          setAgency(params.agency);
        }
        if (params.requester_interest) {
          setRequesterInterest(params.requester_interest);
        }
        if (params.use_enhanced_processing !== undefined) {
          setUseEnhancedProcessing(params.use_enhanced_processing);
        }
      }

      // Restore result
      if (restoredQuery.result) {
        setResult(restoredQuery.result);

        // Restore editable redactions if available
        if (restoredQuery.result.redactions) {
          setEditableRedactions(restoredQuery.result.redactions);
        } else if (restoredQuery.result.final_redactions) {
          setEditableRedactions(restoredQuery.result.final_redactions);
        }
      }

      // If this was a PDF upload, fetch the stored PDF so the editor works
      const fileHash = params?.file_hash;
      const filename = params?.filename;
      if (fileHash) {
        const apiBase = getApiBaseUrl();
        // R2-AUTH: /foia/pdf/{hash} now requires a Bearer token
        const pdfAuthToken = localStorage.getItem('authToken');
        fetch(`${apiBase}/foia/pdf/${fileHash}`, {
          headers: pdfAuthToken ? { Authorization: `Bearer ${pdfAuthToken}` } : {},
        })
          .then(res => {
            if (!res.ok) throw new Error('PDF not found');
            return res.blob();
          })
          .then(blob => {
            const pdfFile = new File([blob], filename || `${fileHash}.pdf`, { type: 'application/pdf' });
            setCurrentPDFFile(pdfFile);
          })
          .catch(err => {
            // PDF editor won't be available, but text editor still works
          });
      }

      // Clear the location state to prevent re-restoration on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setSelectedUploadFile(file);
    setProcessing(true);
    setProcessingProgress(10);
    setProcessingMessage('Uploading document...');
    setError(null);
    setResult(null);
    setRedactedPDFData(null);
    setCurrentPDFFile(file);

    try {
      setProcessingProgress(25);
      setProcessingMessage('Analyzing document for FOIA exemptions...');
      // Use PDF processing endpoint for ALL file types
      // Non-PDF files are automatically converted to PDF for coordinate-based redaction
      const pdfResult = await processPDFForFOIA(file, agency, requesterInterest);

      // Handle converted files - create a File object from base64 for PDF editor
      if (pdfResult.converted_pdf_base64) {
        // Convert base64 to Blob/File for PDF editor
        const byteCharacters = atob(pdfResult.converted_pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Create a File object with the original filename + .pdf extension
        const originalName = pdfResult.original_filename || file.name;
        const baseName = originalName.replace(/\.[^/.]+$/, '');
        const pdfFile = new File([blob], `${baseName}_converted.pdf`, { type: 'application/pdf' });
        setCurrentPDFFile(pdfFile);

      }

      setProcessingProgress(80);
      setProcessingMessage('Mapping redactions to document...');

      setResult(pdfResult.text_analysis);
      setOriginalDocumentText(pdfResult.text_analysis.original_text || '');

      // Use coordinate_redactions if available (accurate PDF coordinates), otherwise fall back to text redactions
      const redactionsToUse = pdfResult.text_analysis.coordinate_redactions || pdfResult.text_analysis.redactions || [];
      setEditableRedactions(redactionsToUse);

      // Also set up legacy data for backward compatibility
      if (processingMode === 'redact') {
        // For backward compatibility with existing UI components
        setRedactedPDFData({
          textAnalysis: pdfResult.text_analysis,
          redactionCount: pdfResult.redaction_count,
          exemptionsUsed: pdfResult.exemptions_used,
          wasConverted: !!pdfResult.converted_pdf_base64,
          conversionMetadata: pdfResult.conversion,
          mappingStats: pdfResult.mappingStats
        });
      }
      setProcessingProgress(100);
      setProcessingMessage('Complete');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process FOIA document');
    } finally {
      setProcessing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/rtf': ['.rtf'],
      'message/rfc822': ['.eml'],
      'application/vnd.ms-outlook': ['.msg'],
      'text/html': ['.html', '.htm'],
      'text/markdown': ['.md'],
      'text/csv': ['.csv'],
      'application/json': ['.json']
    },
    multiple: false
  });

  const handleTextSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;

    setProcessing(true);
    setProcessingProgress(10);
    setProcessingMessage('Analyzing text for FOIA exemptions...');
    setProcessingPhase(null);
    setError(null);
    setResult(null);

    try {
      let response;
      if (useEnhancedProcessing) {
        setProcessingProgress(20);
        setProcessingPhase('rule_retrieval');
        setProcessingMessage('Retrieving FOIA rules and exemption patterns...');

        // Use streaming endpoint for real-time phase updates
        const apiBase = getApiBaseUrl();
        const authToken = localStorage.getItem('authToken');
        const sseHeaders = { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' };
        if (authToken) sseHeaders['Authorization'] = `Bearer ${authToken}`;

        const streamResponse = await fetch(`${apiBase}/foia/process/enhanced/stream`, {
          method: 'POST',
          headers: sseHeaders,
          body: JSON.stringify({
            text,
            agency,
            requester_interest: requesterInterest,
            use_hyde: true,
            conflict_resolution: conflictResolution,
            conflict_resolution_mode: conflictResolutionMode
          })
        });

        if (!streamResponse.ok) {
          throw new Error(`Server error: ${streamResponse.status}`);
        }

        const reader = streamResponse.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamComplete = false;
        let streamResult = null;

        while (!streamComplete) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));

              if (data.error) {
                throw new Error(data.error);
              }

              // Update processing phase from SSE event
              if (data.processing_phase) {
                setProcessingPhase(data.processing_phase);
              }
              if (data.message) {
                setProcessingMessage(data.message);
              }

              // Map status to progress percentage
              const progressMap = { phase1: 20, phase2: 45, phase3: 70, conflicts: 85, complete: 100 };
              if (data.status && progressMap[data.status]) {
                setProcessingProgress(progressMap[data.status]);
              }

              if (data.status === 'complete' && data.result) {
                streamResult = data.full_result || data.result;
                streamComplete = true;
              }
            } catch (parseErr) {
              if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
            }
          }
        }

        // The stream's terminal event carries the full response payload.
        // Only fall back to the POST (which re-runs the entire pipeline)
        // when talking to an older backend that sent counts only.
        if (streamResult && streamResult.redactions) {
          response = streamResult;
        } else {
          const enhancedResponse = await api.post('/foia/process/enhanced', {
            text,
            agency,
            requester_interest: requesterInterest,
            use_hyde: true,
            conflict_resolution: conflictResolution,
            conflict_resolution_mode: conflictResolutionMode
          });
          response = enhancedResponse.data;
        }

        // Enhanced processing returns a different structure
        setResult({
          ...response,
          original_text: response.original_text || text,
          redactions: response.final_redactions || response.redactions || [],
          // Add enhanced processing specific data
          standard_redactions: response.standard_redactions || [],
          rule_redactions: response.rule_redactions || [],
          conflicts: response.conflicts || [],
          conflict_summary: response.conflict_summary || {},
          enhanced_processing: true
        });

        // Set up for interactive editing with final redactions
        setOriginalDocumentText(response.original_text || text);
        setEditableRedactions(response.final_redactions || response.redactions || []);
      } else {
        setProcessingProgress(30);
        setProcessingMessage('Running standard FOIA redaction analysis...');
        // Use standard rule-based processing
        response = await processFOIADocument(text, agency, requesterInterest, true);
        setResult(response);
        // Set up for interactive editing
        setOriginalDocumentText(response.original_text || text);
        setEditableRedactions(response.redactions || []);
      }
      setProcessingProgress(100);
      setProcessingMessage('Complete');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to process FOIA document');
    } finally {
      setProcessing(false);
      setProcessingPhase(null);
    }
  };

  const EXEMPTION_LABELS = {
    'b1': '(b)(1)', 'b2': '(b)(2)', 'b3': '(b)(3)', 'b4': '(b)(4)',
    'b5': '(b)(5)', 'b6': '(b)(6)', 'b7': '(b)(7)', 'b8': '(b)(8)', 'b9': '(b)(9)',
    '(b)(1)': '(b)(1)', '(b)(2)': '(b)(2)', '(b)(3)': '(b)(3)', '(b)(4)': '(b)(4)',
    '(b)(5)': '(b)(5)', '(b)(6)': '(b)(6)', '(b)(7)': '(b)(7)', '(b)(8)': '(b)(8)', '(b)(9)': '(b)(9)',
  };

  const EXEMPTION_DESCRIPTIONS = {
    '(b)(1)': 'National Security', '(b)(2)': 'Internal Rules', '(b)(3)': 'Statutory',
    '(b)(4)': 'Trade Secrets', '(b)(5)': 'Privileged', '(b)(6)': 'Personal Privacy',
    '(b)(7)': 'Law Enforcement', '(b)(8)': 'Financial', '(b)(9)': 'Geological',
  };

  const handleGenerateLetter = async () => {
    setLetterLoading(true);
    try {
      const data = await generateFOIALetter({
        ...letterForm,
        redaction_result: result,
      });
      setLetterResult(data);
    } catch (err) {
      console.error('Letter generation failed:', err);
    } finally {
      setLetterLoading(false);
    }
  };

  const PHASE_LABELS = {
    rule_retrieval: 'Retrieving Rules',
    llm_analysis: 'LLM Analysis',
    coordinate_mapping: 'Mapping Coordinates',
    conflict_resolution: 'Resolving Conflicts',
    complete: 'Complete',
  };

  const formatExemption = (exemption) => {
    if (!exemption || typeof exemption !== 'string') return 'Unspecified';
    const normalized = EXEMPTION_LABELS[exemption.toLowerCase().trim()] || EXEMPTION_LABELS[exemption.trim()];
    return normalized || exemption;
  };

  const getExemptionDescription = (exemption) => {
    const label = formatExemption(exemption);
    return EXEMPTION_DESCRIPTIONS[label] || '';
  };

  const getExemptionBadgeColor = (exemption) => {
    if (!exemption || typeof exemption !== 'string') {
      return 'bg-gray-100 text-gray-800';
    }

    const colors = {
      'b1': 'bg-red-100 text-red-800',
      'b2': 'bg-orange-100 text-orange-800',
      'b3': 'bg-yellow-100 text-yellow-800',
      'b4': 'bg-green-100 text-green-800',
      'b5': 'bg-blue-100 text-blue-800',
      'b6': 'bg-indigo-100 text-indigo-800',
      'b7': 'bg-purple-100 text-purple-800',
      'b8': 'bg-pink-100 text-pink-800',
      'b9': 'bg-teal-100 text-teal-800'
    };
    const key = exemption.toLowerCase().replace(/[()]/g, '');
    return colors[key] || 'bg-gray-100 text-gray-800';
  };


  const handleRuleClick = async (ruleId) => {
    if (!ruleId) return;
    
    setLoadingRule(true);
    try {
      const ruleData = await getFOIARule(ruleId);
      setSelectedRule(ruleData);
      setRuleModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch rule:', err);
      // Could add error toast here
    } finally {
      setLoadingRule(false);
    }
  };

  const closeRuleModal = () => {
    setRuleModalOpen(false);
    setSelectedRule(null);
  };

  const copyRuleJSON = () => {
    if (selectedRule) {
      navigator.clipboard.writeText(JSON.stringify(selectedRule, null, 2));
      // Could add a toast notification here
    }
  };

  // Helper function to parse multiple rule IDs from combined strings
  const parseRuleIds = (ruleIdString) => {
    if (!ruleIdString || typeof ruleIdString !== 'string') {
      return [];
    }
    
    // Split by common separators and clean up
    const rules = ruleIdString
      .split(/[\+\,\;\|]/)  // Split on +, ,, ;, |
      .map(rule => rule.trim())
      .filter(rule => rule && rule !== '' && rule !== 'None' && !rule.includes('directly applicable'))
      .map(rule => {
        // Clean up common patterns
        if (rule.includes(' + ')) {
          return rule.split(' + ')[0].trim();
        }
        return rule;
      })
      .filter((rule, index, array) => array.indexOf(rule) === index); // Remove duplicates
    
    return rules;
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-100 mb-2">FOIA Document Processing</h1>
        <p className="text-gray-400">
          Analyze documents for FOIA exemptions and generate production-ready redacted PDFs.
        </p>
        
        {/* Processing Mode Selection */}
        <div className="mt-4 flex items-center space-x-6">
          <div className="flex items-center">
            <input
              id="approval-workflow"
              type="checkbox"
              checked={showApprovalWorkflow}
              onChange={(e) => setShowApprovalWorkflow(e.target.checked)}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-600 rounded bg-gray-800"
            />
            <label htmlFor="approval-workflow" className="ml-2 text-sm text-gray-400">
              <span className="font-medium text-gray-300">Man-in-the-loop review</span> - Step through each redaction individually
            </label>
          </div>
        </div>

        {/* Enhanced Processing Toggle */}
        <div className="mt-4 p-4 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="flex items-center">
            <input
              id="enhanced-processing"
              type="checkbox"
              checked={useEnhancedProcessing}
              onChange={(e) => setUseEnhancedProcessing(e.target.checked)}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-600 rounded bg-gray-800"
            />
            <label htmlFor="enhanced-processing" className="ml-3 text-sm">
              <span className="font-medium text-gray-200">Enhanced FOIA Processing</span>
              <p className="text-gray-400 text-xs mt-1">
                Three-phase approach: (1) Standard FOIA exemption detection → (2) Rule-based redactions → (3) Conflict detection & resolution
              </p>
            </label>
          </div>

          {/* Conflict Resolution Dropdown - only visible when enhanced processing is on */}
          {useEnhancedProcessing && (
            <div className="mt-3 flex items-center">
              <label htmlFor="conflict-resolution" className="text-sm text-gray-400 mr-3 whitespace-nowrap">
                Conflict Resolution:
              </label>
              <select
                id="conflict-resolution"
                value={conflictResolution}
                onChange={(e) => setConflictResolution(e.target.value)}
                className="bg-gray-700 border border-gray-600 text-gray-200 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 px-3 py-1.5"
              >
                <option value="merge">Merge (Default)</option>
                <option value="conservative">Conservative</option>
                <option value="expansive">Expansive</option>
              </select>
            </div>
          )}

          {/* F10: Redaction source selector - only visible when enhanced processing is on */}
          {useEnhancedProcessing && (
            <div className="mt-3 flex items-center">
              <label htmlFor="conflict-resolution-mode" className="text-sm text-gray-400 mr-3 whitespace-nowrap">
                Redaction Source:
              </label>
              <select
                id="conflict-resolution-mode"
                value={conflictResolutionMode}
                onChange={(e) => setConflictResolutionMode(e.target.value)}
                className="bg-gray-700 border border-gray-600 text-gray-200 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 px-3 py-1.5"
              >
                <option value="both">Standard + Rule-Based (Default)</option>
                <option value="standard_only">Standard Exemptions Only</option>
                <option value="rule_only">Rule-Based Only</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Panel */}
        <div className="space-y-6">
          {/* Document Upload */}
          <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
            <h2 className="text-lg font-semibold text-gray-100 mb-4 flex items-center">
              <DocumentArrowUpIcon className="w-5 h-5 mr-2 text-amber-400" />
              Upload Document
            </h2>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-400 bg-blue-50'
                  : selectedUploadFile
                  ? 'border-[var(--vault-border,#374151)] bg-[var(--vault-card,#1f2937)]'
                  : 'border-gray-600 hover:border-gray-500'
              }`}
            >
              <input {...getInputProps()} />
              {selectedUploadFile ? (
                <div className="flex items-center justify-center space-x-3">
                  <DocumentTextIcon className="w-10 h-10 text-[var(--spotlight-gold,#d4a843)] flex-shrink-0" />
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium text-[var(--spotlight-gold,#d4a843)] truncate" title={selectedUploadFile.name}>
                      {selectedUploadFile.name.length > 40 ? selectedUploadFile.name.substring(0, 37) + '...' : selectedUploadFile.name}
                    </p>
                    <p className="text-xs text-[var(--text-secondary,#9ca3af)]">
                      {formatFileSize(selectedUploadFile.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUploadFile(null);
                      setCurrentPDFFile(null);
                    }}
                    className="p-1 rounded-full hover:bg-gray-700/50 transition-colors flex-shrink-0"
                    title="Remove file"
                  >
                    <XMarkIcon className="w-5 h-5 text-[var(--text-secondary,#9ca3af)] hover:text-[var(--text-primary,#f3f4f6)]" />
                  </button>
                </div>
              ) : (
                <>
                  <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-500" />
                  <p className="mt-2 text-sm text-gray-400">
                    {isDragActive
                      ? 'Drop the document here...'
                      : 'Drag & drop a document here, or click to select'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supports PDF, Word (DOC/DOCX), Email (EML/MSG), RTF, TXT, HTML, MD, CSV, JSON
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Text Input */}
          <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
            <h2 className="text-lg font-semibold text-gray-100 mb-4">Or Enter Text Directly</h2>
            <form onSubmit={handleTextSubmit} className="space-y-4">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste document text here..."
                className="w-full h-40 p-3 border border-gray-600 rounded-md bg-gray-800 text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
              
              {/* Processing Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Agency
                  </label>
                  <input
                    type="text"
                    value={agency}
                    onChange={(e) => setAgency(e.target.value)}
                    placeholder="e.g., CIA, FBI, DOD"
                    className="w-full p-2 border border-gray-600 rounded-md bg-gray-800 text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Specifying an agency improves FOIA rule matching accuracy</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Requester Interest
                  </label>
                  <input
                    type="text"
                    value={requesterInterest}
                    onChange={(e) => setRequesterInterest(e.target.value)}
                    placeholder="e.g., journalism, research"
                    className="w-full p-2 border border-gray-600 rounded-md bg-gray-800 text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Helps calibrate public interest balancing for exemption decisions</p>
                </div>
              </div>


              <button
                type="submit"
                disabled={processing || !text.trim()}
                className="w-full bg-amber-600 text-white py-2 px-4 rounded-md hover:bg-amber-500 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
              >
                {processing ? 'Processing...' : 'Process Document'}
              </button>
            </form>
          </div>
        </div>

        {/* Results Panel */}
        <div className="space-y-6">
          {error && (
            <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-red-400">Processing Error</h3>
                  <p className="text-sm text-red-300 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          <ProcessingStatus
            isProcessing={processing}
            progress={processingProgress}
            message={processingMessage}
          />

          {processing && processingPhase && processingPhase !== 'complete' && (
            <div className="flex justify-center mt-2 mb-2">
              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-900/40 text-blue-300 border border-blue-700">
                {PHASE_LABELS[processingPhase] || processingPhase}
              </span>
            </div>
          )}

          {redactedPDFData && (
            <div className="space-y-4 mb-6">
              <div className="bg-green-950/30 border border-green-900/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-green-400 mb-2">PDF Processed for FOIA Review</h3>
                    {redactedPDFData.wasConverted && (
                      <p className="text-sm text-blue-300 bg-blue-950/30 border border-blue-900/50 rounded px-2 py-1 mb-2 inline-block">
                        Converted from {redactedPDFData.conversionMetadata?.original_format?.toUpperCase() || 'non-PDF'} to PDF
                      </p>
                    )}
                    <p className="text-sm text-green-300">
                      Document processed with {redactedPDFData.redactionCount} redactions using exemptions: {redactedPDFData.exemptionsUsed.join(', ')}
                    </p>
                    <p className="text-xs text-green-400/70 mt-1">
                      Use the PDF Editor below to review redactions and download the final PDF
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Visual Redaction Mapping Statistics */}
              <MappingFailureDisplay 
                mappingStats={redactedPDFData.mappingStats}
              />
            </div>
          )}

          {result && (
            <div className="bg-gray-900 rounded-lg border border-gray-700 p-6 space-y-6">
              {/* Header + Compact Metrics Strip */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-100 flex items-center">
                    <EyeSlashIcon className="w-5 h-5 mr-2 text-amber-400" />
                    FOIA Analysis Results
                  </h2>
                  <div className="flex items-center gap-2">
                    {result?.provenance_decision_id && (
                      <a
                        href={`/provenance/${encodeURIComponent(result.provenance_decision_id)}`}
                        className="px-3 py-1.5 text-sm text-blue-300 border border-blue-700 rounded-md hover:bg-blue-900/30"
                      >
                        View provenance
                      </a>
                    )}
                    <button
                      onClick={() => { setLetterResult(null); setShowLetterModal(true); }}
                      className="px-4 py-2 text-sm font-medium text-[var(--spotlight-gold)] border border-[var(--spotlight-gold)] rounded-md hover:bg-[var(--spotlight-gold)]/10"
                    >
                      Generate Response Letter
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-amber-400">{result.total_redactions || result.redactions?.length || 0}</span>
                    <span className="text-xs text-gray-400">Redactions</span>
                  </div>
                  <div className="w-px h-8 bg-gray-700" />
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-green-400">{Math.round((result.confidence_score || 0) * 100)}%</span>
                    <span className="text-xs text-gray-400">Confidence</span>
                  </div>
                  <div className="w-px h-8 bg-gray-700" />
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-blue-400">{Math.round((result.processing_time || 0) * 100) / 100}s</span>
                    <span className="text-xs text-gray-400">Time</span>
                  </div>
                  {result.enhanced_processing && (
                    <>
                      <div className="w-px h-8 bg-gray-700" />
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-purple-400">{result.standard_redactions?.length || 0}</span>
                        <span className="text-xs text-gray-400">Standard</span>
                      </div>
                      <div className="w-px h-8 bg-gray-700" />
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-indigo-400">{result.rule_redactions?.length || 0}</span>
                        <span className="text-xs text-gray-400">Rule-Based</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Exemptions Summary — moved up for overview */}
              {result.exemptions_summary && Object.keys(result.exemptions_summary).length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Exemptions Applied</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(result.exemptions_summary).map(([exemption, count]) => (
                      <div key={exemption || 'unspecified'} className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg border border-gray-700">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getExemptionBadgeColor(exemption)}`}>
                          {formatExemption(exemption)}
                        </span>
                        <span className="text-lg font-bold text-gray-100">{count || 0}</span>
                        {getExemptionDescription(exemption) && (
                          <span className="text-xs text-gray-500">{getExemptionDescription(exemption)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Editor CTA */}
              {originalDocumentText && editableRedactions.length > 0 && (
                <div className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-lg">
                  <div className="flex items-center">
                    <PencilSquareIcon className="w-5 h-5 text-amber-400 mr-2" />
                    <span className="font-medium text-gray-100">Review & Edit Redactions</span>
                    <span className="ml-2 text-sm text-gray-400">Visual editor with approval workflow</span>
                  </div>
                  {currentPDFFile ? (
                    <button
                      onClick={() => setShowPDFRedactionEditor(true)}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-md hover:bg-amber-500 transition-colors"
                    >
                      <PencilSquareIcon className="h-4 w-4 mr-1.5" />
                      Open PDF Editor
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowRedactionEditor(true)}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-md hover:bg-amber-500 transition-colors"
                    >
                      <PencilSquareIcon className="h-4 w-4 mr-1.5" />
                      Open Editor
                    </button>
                  )}
                </div>
              )}

              {/* Editor Modals */}
              {showRedactionEditor && (
                <FullScreenRedactionEditor
                  initialText={originalDocumentText}
                  initialRedactions={editableRedactions}
                  onRedactionsChange={setEditableRedactions}
                  onSave={() => setShowRedactionEditor(false)}
                  onClose={() => setShowRedactionEditor(false)}
                  showApprovalWorkflow={showApprovalWorkflow}
                />
              )}
              {showPDFRedactionEditor && currentPDFFile && (
                <PDFRedactionEditor
                  pdfFile={currentPDFFile}
                  initialRedactions={editableRedactions}
                  onRedactionsChange={setEditableRedactions}
                  onSave={async (finalRedactions) => {
                    try {
                      await generateRedactedPDF({ pdf_file: currentPDFFile, redactions: finalRedactions, agency, requester_interest: requesterInterest });
                      setShowPDFRedactionEditor(false);
                    } catch (error) {
                      setError('Failed to generate redacted PDF');
                    }
                  }}
                  onClose={() => setShowPDFRedactionEditor(false)}
                  showApprovalWorkflow={showApprovalWorkflow}
                  agency={agency}
                  requesterInterest={requesterInterest}
                />
              )}

              {/* Grouped Redactions */}
              {result.redactions && result.redactions.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-300">
                      Redaction Details ({result.redactions.length})
                    </h3>
                    <div className="flex gap-2">
                      <button onClick={expandAllGroups} className="text-xs text-amber-400 hover:text-amber-300">Expand All</button>
                      <span className="text-gray-600">|</span>
                      <button onClick={collapseAllGroups} className="text-xs text-amber-400 hover:text-amber-300">Collapse All</button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {Object.entries(groupedRedactions).map(([exemptionKey, redactions]) => (
                      <div key={exemptionKey} className="border border-gray-700 rounded-lg overflow-hidden">
                        {/* Group Header */}
                        <button
                          onClick={() => toggleGroup(exemptionKey)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3">
                            {expandedGroups.has(exemptionKey) ? (
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                            )}
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExemptionBadgeColor(exemptionKey)}`}>
                              {formatExemption(exemptionKey)}
                            </span>
                            {getExemptionDescription(exemptionKey) && (
                              <span className="text-sm text-gray-400">{getExemptionDescription(exemptionKey)}</span>
                            )}
                          </div>
                          <span className="text-sm font-medium text-gray-400">{redactions.length} redaction{redactions.length !== 1 ? 's' : ''}</span>
                        </button>

                        {/* Group Body — compact rows */}
                        {expandedGroups.has(exemptionKey) && (
                          <div className="divide-y divide-gray-800">
                            {redactions.map((redaction) => {
                              const isExpanded = expandedRedactions.has(redaction._idx);
                              // "STANDARD-..." is a synthetic provenance tag from the standard-exemption
    // detector, not a retrievable rule — rendering it as a rule link 404s.
    const isGeneralExemption = !redaction.rule_id || redaction.rule_id === 'GENERAL' || redaction.rule_id === ''
      || redaction.rule_id.startsWith('STANDARD');
                              const hasWarning = redaction.justification?.includes('[WARNING:');
                              const truncatedText = (redaction.original_text || '').length > 80
                                ? redaction.original_text.substring(0, 77) + '...'
                                : redaction.original_text || '';

                              return (
                                <div key={redaction._idx} className="bg-gray-900">
                                  {/* Compact row */}
                                  <button
                                    onClick={() => toggleRedaction(redaction._idx)}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800/50 transition-colors text-left"
                                  >
                                    {isExpanded ? (
                                      <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                    ) : (
                                      <ChevronRightIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                    )}
                                    <span className="text-sm text-red-400 font-mono truncate flex-1 min-w-0">{truncatedText}</span>
                                    <span className="text-xs text-gray-500 flex-shrink-0">{Math.round((redaction.confidence || 0) * 100)}%</span>
                                    {hasWarning && (
                                      <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                                    )}
                                    {redaction.rule_id && !isGeneralExemption && (
                                      <span className="text-xs text-blue-400 font-mono flex-shrink-0">{parseRuleIds(redaction.rule_id)[0]}</span>
                                    )}
                                    {isGeneralExemption && (
                                      <span className="text-xs text-orange-400 flex-shrink-0">General</span>
                                    )}
                                  </button>

                                  {/* Expanded detail */}
                                  {isExpanded && (
                                    <div className="px-4 pb-4 pt-1 ml-7 space-y-3 border-l-2 border-gray-700">
                                      {/* Original text */}
                                      <div>
                                        <p className="text-xs font-medium text-gray-500 mb-1">REDACTED TEXT</p>
                                        <div className="bg-red-950/30 border border-red-900/50 rounded p-2">
                                          <p className="text-sm text-red-300 font-mono">{redaction.original_text}</p>
                                        </div>
                                      </div>
                                      {/* Replacement */}
                                      <div>
                                        <p className="text-xs font-medium text-gray-500 mb-1">REPLACED WITH</p>
                                        <div className="bg-gray-800 border border-gray-700 rounded p-2">
                                          <p className="text-sm text-gray-300 font-mono">{redaction.redacted_text}</p>
                                        </div>
                                      </div>
                                      {/* Rule link */}
                                      {redaction.rule_id && !isGeneralExemption && (
                                        <div className="flex flex-wrap gap-2">
                                          {parseRuleIds(redaction.rule_id).map((ruleId, ri) => (
                                            <button
                                              key={ri}
                                              onClick={(e) => { e.stopPropagation(); handleRuleClick(ruleId); }}
                                              className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300 font-mono"
                                              disabled={loadingRule}
                                            >
                                              <LinkIcon className="w-3.5 h-3.5 mr-1" />{ruleId}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                      {/* Justification */}
                                      {redaction.justification && (
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">JUSTIFICATION</p>
                                          {(() => {
                                            const justification = redaction.justification;
                                            const warningMatch = justification.match(/\[WARNING: ([^\]]+)\]/);
                                            if (warningMatch) {
                                              const warningText = warningMatch[1];
                                              const clean = justification.replace(/\[WARNING: [^\]]+\]/, '').trim();
                                              return (
                                                <div className="space-y-2">
                                                  <div className="bg-red-950/30 border border-red-900/50 rounded p-2 flex items-start gap-2">
                                                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                      <p className="text-xs font-medium text-red-400">CITATION MISMATCH</p>
                                                      <p className="text-xs text-red-300 mt-1">{warningText}</p>
                                                    </div>
                                                  </div>
                                                  {clean && <p className="text-sm text-gray-300 bg-gray-800 border border-gray-700 rounded p-2">{clean}</p>}
                                                </div>
                                              );
                                            }
                                            return <p className="text-sm text-gray-300 bg-gray-800 border border-gray-700 rounded p-2">{justification}</p>;
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Applied FOIA Rules — collapsible */}
              {result.applied_rules && result.applied_rules.length > 0 && (
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer px-4 py-3 bg-gray-800 rounded-lg border border-gray-700 hover:bg-gray-750 transition-colors">
                    <span className="text-sm font-medium text-gray-300">FOIA Rules Applied ({result.applied_rules.length})</span>
                    <ChevronDownIcon className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-2 space-y-2">
                    {result.applied_rules.map((rule, index) => (
                      <div key={index} className="flex items-start justify-between p-3 bg-gray-800 border border-gray-700 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <button
                              onClick={() => handleRuleClick(rule.rule_id)}
                              className="font-mono text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
                              disabled={loadingRule}
                            >
                              {rule.rule_id}
                            </button>
                            {rule.exemption_category && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getExemptionBadgeColor(rule.exemption_category)}`}>
                                {formatExemption(rule.exemption_category)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">{rule.description}</p>
                          {rule.source && <p className="text-xs text-gray-500 mt-1">Source: {rule.source}</p>}
                        </div>
                        <button onClick={() => handleRuleClick(rule.rule_id)} className="p-1 hover:bg-gray-700 rounded transition-colors" disabled={loadingRule}>
                          <LinkIcon className="w-4 h-4 text-blue-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Processing Details Accordion */}
              {(result.public_interest_analysis || result.balancing_rationale || result.enhanced_processing || (result.conflicts && result.conflicts.length > 0)) && (
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer px-4 py-3 bg-gray-800 rounded-lg border border-gray-700 hover:bg-gray-750 transition-colors">
                    <span className="text-sm font-medium text-gray-300">Processing Details & Analysis</span>
                    <ChevronDownIcon className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-2 space-y-4">
                    {result.public_interest_analysis && (
                      <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                        <div className="flex items-center mb-2">
                          <CheckCircleIcon className="w-4 h-4 text-blue-400 mr-2" />
                          <span className="text-sm font-medium text-gray-300">Public Interest Analysis</span>
                        </div>
                        <p className="text-sm text-gray-400">{result.public_interest_analysis}</p>
                      </div>
                    )}
                    {result.balancing_rationale && (
                      <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                        <div className="flex items-center mb-2">
                          <CheckCircleIcon className="w-4 h-4 text-green-400 mr-2" />
                          <span className="text-sm font-medium text-gray-300">Balancing Rationale</span>
                        </div>
                        <p className="text-sm text-gray-400">{result.balancing_rationale}</p>
                      </div>
                    )}
                    {result.enhanced_processing && (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 bg-gray-800 border border-gray-700 rounded-lg text-center">
                          <div className="text-xl font-bold text-blue-400">{result.standard_redactions?.length || 0}</div>
                          <div className="text-xs text-gray-500">Phase 1: Standard</div>
                        </div>
                        <div className="p-3 bg-gray-800 border border-gray-700 rounded-lg text-center">
                          <div className="text-xl font-bold text-purple-400">{result.rule_redactions?.length || 0}</div>
                          <div className="text-xs text-gray-500">Phase 2: Rule-Based</div>
                        </div>
                        <div className="p-3 bg-gray-800 border border-gray-700 rounded-lg text-center">
                          <div className="text-xl font-bold text-green-400">{result.redactions?.length || 0}</div>
                          <div className="text-xs text-gray-500">Phase 3: Merged</div>
                        </div>
                      </div>
                    )}
                    {result.conflict_summary && Object.keys(result.conflict_summary).length > 0 && (
                      <div className="p-4 bg-yellow-950/20 border border-yellow-900/50 rounded-lg">
                        <div className="flex items-center mb-2">
                          <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500 mr-2" />
                          <span className="text-sm font-medium text-yellow-400">Conflicts: {result.conflict_summary.total_conflicts || 0} total, {result.conflict_summary.auto_resolved || 0} auto-resolved</span>
                        </div>
                        {result.conflict_summary.resolution_strategy && (
                          <p className="text-xs text-yellow-500">Strategy: {result.conflict_summary.resolution_strategy}</p>
                        )}
                      </div>
                    )}
                    {result.conflicts && result.conflicts.length > 0 && (
                      <div className="space-y-2">
                        {result.conflicts.map((conflict, index) => (
                          <details key={index} className="group p-3 bg-gray-800 border border-gray-700 rounded-lg">
                            <summary className="flex items-center justify-between cursor-pointer list-none">
                              <span className="text-sm font-medium text-gray-200 capitalize">
                                Conflict {index + 1}: {(conflict.conflict_type || 'unclassified').replace(/_/g, ' ')}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-300 border border-yellow-500/20 whitespace-nowrap">
                                {conflict.severity || 'unrated'} severity
                              </span>
                            </summary>
                            <p className="mt-2 text-xs leading-relaxed text-gray-400 whitespace-pre-wrap">
                              {(conflict.description || '').replace(/\*\*/g, '')}
                            </p>
                          </details>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Recommendations */}
              {result.recommendations && result.recommendations.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-300 mb-2">Recommendations</h3>
                  <ul className="space-y-1.5">
                    {result.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start">
                        <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 mr-2.5 flex-shrink-0" />
                        <span className="text-sm text-gray-400">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Raw Redacted Text */}
              {result.redacted_text && (
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer px-4 py-3 bg-gray-800 rounded-lg border border-gray-700 hover:bg-gray-750 transition-colors">
                    <span className="text-sm font-medium text-gray-300">Suggested Redactions (Raw Text)</span>
                    <ChevronDownIcon className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-2 bg-gray-800 p-4 rounded-lg border border-gray-700 max-h-96 overflow-y-auto">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">{result.redacted_text}</pre>
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rule Details Modal */}
      {ruleModalOpen && selectedRule && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-gray-700 w-11/12 max-w-4xl shadow-lg rounded-md bg-gray-900">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-100">
                FOIA Rule Details: {selectedRule.rule_id || 'Unknown ID'}
              </h3>
              <button
                onClick={closeRuleModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            {/* Header Information */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {selectedRule.exemption_category && (
                  <div className="text-center p-3 bg-gray-800 rounded-lg border border-gray-700">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExemptionBadgeColor(selectedRule.exemption_category.replace(/[()]/g, ''))}`}>
                      {selectedRule.exemption_category}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">Exemption Category</p>
                  </div>
                )}
                {selectedRule.context_domain && (
                  <div className="text-center p-3 bg-gray-800 rounded-lg border border-gray-700">
                    <p className="text-sm font-medium text-indigo-300">{selectedRule.context_domain}</p>
                    <p className="text-xs text-gray-500 mt-1">Domain</p>
                  </div>
                )}
                {selectedRule.source && (
                  <div className="text-center p-3 bg-gray-800 rounded-lg border border-gray-700">
                    <p className="text-sm font-medium text-green-300">{selectedRule.source}</p>
                    <p className="text-xs text-gray-500 mt-1">Source Document</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rule Title */}
            {selectedRule.rule_title && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Rule Title:</h4>
                <p className="text-base font-medium text-gray-100 bg-gray-800 border border-gray-700 p-3 rounded-md">{selectedRule.rule_title}</p>
              </div>
            )}

            {/* Description */}
            {(selectedRule.rule_description || selectedRule.description) && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Description:</h4>
                <p className="text-sm text-gray-300 bg-blue-900/30 border border-blue-800/50 p-3 rounded-md leading-relaxed">
                  {selectedRule.rule_description || selectedRule.description}
                </p>
              </div>
            )}

            {/* Redaction Criteria */}
            {selectedRule.redaction_criteria && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Redaction Criteria:</h4>
                <p className="text-sm text-gray-300 bg-purple-900/30 border border-purple-800/50 p-3 rounded-md">
                  {selectedRule.redaction_criteria}
                </p>
              </div>
            )}

            {/* Keywords */}
            {selectedRule.keywords && selectedRule.keywords.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Keywords:</h4>
                <div className="flex flex-wrap gap-1">
                  {selectedRule.keywords.slice(0, 12).map((keyword, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-800 text-gray-300 border border-gray-700">
                      {keyword}
                    </span>
                  ))}
                  {selectedRule.keywords.length > 12 && (
                    <span className="text-xs text-gray-500 px-2 py-1">
                      +{selectedRule.keywords.length - 12} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Examples */}
            {selectedRule.examples && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Examples:</h4>
                <p className="text-sm text-gray-300 bg-yellow-900/30 border border-yellow-800/50 p-3 rounded-md">
                  {selectedRule.examples}
                </p>
              </div>
            )}

            {/* Agency Guidance */}
            {selectedRule.agency_guidance && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Agency Guidance:</h4>
                <p className="text-sm text-gray-300 bg-blue-900/30 border border-blue-800/50 p-3 rounded-md">
                  {selectedRule.agency_guidance}
                </p>
              </div>
            )}

            {/* Considerations */}
            {selectedRule.considerations && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Considerations:</h4>
                <p className="text-sm text-gray-300 bg-orange-900/30 border border-orange-800/50 p-3 rounded-md">
                  {selectedRule.considerations}
                </p>
              </div>
            )}

            {/* Balancing Test */}
            {selectedRule.balancing_test && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Balancing Test:</h4>
                <p className="text-sm text-gray-300 bg-green-900/30 border border-green-800/50 p-3 rounded-md">
                  {selectedRule.balancing_test}
                </p>
              </div>
            )}

            {/* Legal Citation & Context */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {selectedRule.legal_citation && selectedRule.legal_citation !== 'N/A' && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Legal Citation:</h4>
                  <p className="text-sm text-blue-300 bg-blue-900/30 border border-blue-800/50 p-2 rounded">
                    {selectedRule.legal_citation}
                  </p>
                </div>
              )}
              {selectedRule.context_section && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Context Section:</h4>
                  <p className="text-sm text-gray-300 bg-gray-800 border border-gray-700 p-2 rounded">
                    {selectedRule.context_section}
                  </p>
                </div>
              )}
            </div>

            {/* Advanced Options - Collapsible JSON */}
            <div className="border-t border-gray-700 pt-4">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-400 hover:text-gray-200">
                  <span>Advanced: View Raw JSON</span>
                  <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">Complete rule data in JSON format</p>
                    <button
                      onClick={copyRuleJSON}
                      className="text-xs bg-gray-700 text-gray-200 px-2 py-1 rounded hover:bg-gray-600 transition-colors"
                    >
                      Copy JSON
                    </button>
                  </div>
                  <div className="bg-gray-950 text-green-400 p-3 rounded-lg overflow-auto max-h-64 border border-gray-700">
                    <pre className="text-xs whitespace-pre-wrap font-mono">
                      {JSON.stringify(selectedRule, null, 2)}
                    </pre>
                  </div>
                </div>
              </details>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={closeRuleModal}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FOIA Response Letter Modal */}
      {showLetterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-lg p-6" style={{backgroundColor: 'var(--vault-surface)', border: '1px solid var(--vault-border)'}}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-[var(--text-primary)]">Generate FOIA Response Letter</h3>
              <button onClick={() => setShowLetterModal(false)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] text-2xl leading-none">&times;</button>
            </div>

            {/* Form fields */}
            <div className="space-y-3 mb-4">
              <input placeholder="Requester Name" value={letterForm.requester_name}
                onChange={e => setLetterForm(f => ({...f, requester_name: e.target.value}))}
                className="w-full px-3 py-2 text-sm rounded bg-[var(--vault-bg)] border border-[var(--vault-border)] text-[var(--text-primary)]" />
              <input type="date" value={letterForm.request_date}
                onChange={e => setLetterForm(f => ({...f, request_date: e.target.value}))}
                className="w-full px-3 py-2 text-sm rounded bg-[var(--vault-bg)] border border-[var(--vault-border)] text-[var(--text-primary)]" />
              <input placeholder="Request description" value={letterForm.request_description}
                onChange={e => setLetterForm(f => ({...f, request_description: e.target.value}))}
                className="w-full px-3 py-2 text-sm rounded bg-[var(--vault-bg)] border border-[var(--vault-border)] text-[var(--text-primary)]" />
              <select value={letterForm.response_type}
                onChange={e => setLetterForm(f => ({...f, response_type: e.target.value}))}
                className="w-full px-3 py-2 text-sm rounded bg-[var(--vault-bg)] border border-[var(--vault-border)] text-[var(--text-primary)]">
                <option value="partial_release">Partial Release</option>
                <option value="full_release">Full Release</option>
                <option value="full_denial">Full Denial</option>
              </select>
            </div>

            <button onClick={handleGenerateLetter} disabled={letterLoading || !letterForm.requester_name}
              className="w-full px-4 py-2 text-sm font-medium rounded bg-[var(--spotlight-gold)] text-black hover:opacity-90 disabled:opacity-50 mb-4">
              {letterLoading ? 'Generating...' : 'Generate Letter'}
            </button>

            {letterResult && (
              <>
                <div className="p-4 rounded border border-[var(--vault-border)] bg-[var(--vault-bg)] text-sm text-[var(--text-primary)] whitespace-pre-wrap max-h-64 overflow-y-auto mb-3"
                  dangerouslySetInnerHTML={{__html: letterResult.letter_html}} />
                <div className="flex gap-2">
                  <button onClick={() => navigator.clipboard.writeText(letterResult.letter_text)}
                    className="px-3 py-1.5 text-xs border border-[var(--vault-border)] rounded hover:bg-[var(--vault-elevated)] text-[var(--text-secondary)]">
                    Copy to Clipboard
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}